import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  decideLegalStructureProposal,
  fetchLegalStructureProposals,
  ILegalStructureProposal,
  ILegalStructureProposalDecision,
  LegalStructureProposalStatus,
} from '../../../../services/queries/MaggieLegalStructureQueries';

/**
 * The ledger entity proposal queue (FD-4477).
 *
 * One query per status rather than one fetch filtered client-side: `proposed` is the
 * work list and `rejected` is the audit trail, they are read at different times, and
 * the endpoint filters server-side anyway.
 */

const QUERY_ROOT = 'legalStructureProposals';

const proposalQueryKey = (status: LegalStructureProposalStatus) => [QUERY_ROOT, status];

const NO_PROPOSALS: ILegalStructureProposal[] = [];

export interface IProposalQueueResult {
  proposals: ILegalStructureProposal[];
  isLoading: boolean;
  /** True when the endpoint answered 404/501 — this environment has no proposal feed. */
  isQueueUnavailable: boolean;
  /**
   * The read failed for any other reason. Held apart from an empty list because both
   * arrive here as no rows, and "nothing to review" is the wrong thing to tell someone
   * whose queue merely failed to load.
   */
  isError: boolean;
}

export function useProposalQueue(
  status: LegalStructureProposalStatus,
  isEnabled = true
): IProposalQueueResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: proposalQueryKey(status),
    queryFn: () => fetchLegalStructureProposals(status),
    enabled: isEnabled,
    retry: false,
  });

  return {
    proposals: data ?? NO_PROPOSALS,
    // A disabled query reports `isLoading` true in react-query v5, which would read as
    // "still loading" forever on a tenant without the module.
    isLoading: isEnabled && isLoading,
    isQueueUnavailable: data === null,
    isError,
  };
}

/**
 * Pending count for the module nav badge.
 *
 * Read here rather than threaded down from a surface because the badge has to be right
 * on every Entity Management screen, not just the queue's own. Shares the `proposed`
 * query key with {@link useProposalQueue}, so opening the queue costs no second fetch
 * and a decision updates both at once.
 *
 * `isEnabled` exists because the nav renders before the module gate is known: without
 * it, a tenant with legal structure switched off would still fetch a queue it cannot
 * reach.
 */
export function usePendingProposalCount(isEnabled = true): number {
  const { proposals } = useProposalQueue('proposed', isEnabled);

  return proposals.length;
}

/**
 * Decides one proposal, then refetches every proposal list.
 *
 * Both lists are invalidated for any decision: an accept drops the row out of
 * `proposed` with nothing gained anywhere, while a reject moves it across to
 * `rejected`. Invalidating only the list in view would leave the other stale behind
 * the status filter.
 *
 * Errors are left to the caller — a 409 on a merge and a 403 without `fd.internal`
 * mean different things to the reviewer and cannot share one message.
 */
export function useProposalDecision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      proposalId,
      decision,
    }: {
      proposalId: number;
      decision: ILegalStructureProposalDecision;
    }) => decideLegalStructureProposal(proposalId, decision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_ROOT] }),
  });
}
